import api, { apiUtils } from './api';

export interface Note {
    id: number;
    title: string;
    content: string;
    tagId?: number;
    tag?: {
        id: number;
        name: string;
        color: string;
    };
    synced: boolean;
    lastSyncedAt: string;
    createdAt: string;
    updatedAt: string;
    userId: number;
    username: string;
}

export interface NoteStat {
    totalCount: number;
    monthlyStats: { month: string; count: number }[];
    tagStats: { tag: string; count: number }[];
}

// 网页版离线存储管理
class OfflineNoteManager {
    private static instance: OfflineNoteManager;
    private dbName = 'DailyNotesDB';
    private dbVersion = 1;
    private db: IDBDatabase | null = null;

    static getInstance(): OfflineNoteManager {
        if (!OfflineNoteManager.instance) {
            OfflineNoteManager.instance = new OfflineNoteManager();
        }
        return OfflineNoteManager.instance;
    }

    async initDB(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.dbVersion);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;

                // 创建笔记存储
                if (!db.objectStoreNames.contains('notes')) {
                    const noteStore = db.createObjectStore('notes', { keyPath: 'id' });
                    noteStore.createIndex('userId', 'userId', { unique: false });
                    noteStore.createIndex('category', 'category', { unique: false });
                    noteStore.createIndex('updatedAt', 'updatedAt', { unique: false });
                }

                // 创建待同步队列
                if (!db.objectStoreNames.contains('syncQueue')) {
                    db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
                }
            };
        });
    }

    async saveNoteOffline(note: Note): Promise<void> {
        if (!this.db) await this.initDB();

        const transaction = this.db!.transaction(['notes'], 'readwrite');
        const store = transaction.objectStore('notes');
        await store.put({ ...note, synced: false });
    }

    async getOfflineNotes(): Promise<Note[]> {
        if (!this.db) await this.initDB();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction(['notes'], 'readonly');
            const store = transaction.objectStore('notes');
            const request = store.getAll();

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async addToSyncQueue(action: string, noteId: number, data?: any): Promise<void> {
        if (!this.db) await this.initDB();

        const transaction = this.db!.transaction(['syncQueue'], 'readwrite');
        const store = transaction.objectStore('syncQueue');
        await store.add({
            action,
            noteId,
            data,
            timestamp: Date.now()
        });
    }
}

const offlineManager = OfflineNoteManager.getInstance();

const noteService = {
    async getAllNotes(): Promise<Note[]> {
        try {
            // 优先从网络获取
            if (apiUtils.isOnline()) {
                const response = await api.get('/notes');

                // 缓存到离线存储
                if (response.data && Array.isArray(response.data)) {
                    for (const note of response.data) {
                        await offlineManager.saveNoteOffline(note);
                    }
                }

                return response.data;
            } else {
                // 离线模式，从本地获取
                return await offlineManager.getOfflineNotes();
            }
        } catch (error) {
            // 网络请求失败，尝试从离线存储获取
            try {
                const offlineNotes = await offlineManager.getOfflineNotes();
                if (offlineNotes.length > 0) {
                    return offlineNotes;
                }
            } catch (offlineError) {
                // 静默处理离线获取失败
            }

            throw error;
        }
    },

    async getNoteById(id: number): Promise<Note> {
        try {
            const response = await api.get(`/notes/${id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    async getNotesByCategory(category: string): Promise<Note[]> {
        try {
            const response = await api.get(`/notes/category/${category}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    async getNotesByTag(tag: string): Promise<Note[]> {
        try {
            const response = await api.get(`/notes/tag/${tag}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    async createNote(note: Omit<Note, 'id' | 'userId' | 'username' | 'createdAt' | 'updatedAt' | 'synced' | 'lastSyncedAt'>): Promise<Note> {
        try {
            if (apiUtils.isOnline()) {
                const response = await api.post('/notes', note);
                const createdNote = response.data;

                // 保存到离线存储
                await offlineManager.saveNoteOffline(createdNote);

                return createdNote;
            } else {
                // 离线模式，创建临时笔记
                const tempNote: Note = {
                    ...note,
                    id: Date.now(), // 临时ID
                    userId: 0,
                    username: '',
                    synced: false,
                    lastSyncedAt: '',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                };

                await offlineManager.saveNoteOffline(tempNote);
                await offlineManager.addToSyncQueue('create', tempNote.id, note);

                return tempNote;
            }
        } catch (error) {
            throw error;
        }
    },

    async updateNote(id: number, note: Omit<Note, 'id' | 'userId' | 'username' | 'createdAt' | 'updatedAt' | 'synced' | 'lastSyncedAt'>): Promise<Note> {
        try {
            if (apiUtils.isOnline()) {
                const response = await api.put(`/notes/${id}`, note);
                const updatedNote = response.data;

                // 更新离线存储
                await offlineManager.saveNoteOffline(updatedNote);

                return updatedNote;
            } else {
                // 离线模式，更新本地笔记
                const existingNotes = await offlineManager.getOfflineNotes();
                const existingNote = existingNotes.find(n => n.id === id);

                if (existingNote) {
                    const updatedNote: Note = {
                        ...existingNote,
                        ...note,
                        updatedAt: new Date().toISOString(),
                        synced: false
                    };

                    await offlineManager.saveNoteOffline(updatedNote);
                    await offlineManager.addToSyncQueue('update', id, note);

                    return updatedNote;
                }

                throw new Error('笔记不存在');
            }
        } catch (error) {
            throw error;
        }
    },

    async deleteNote(id: number): Promise<void> {
        try {
            if (apiUtils.isOnline()) {
                await api.delete(`/notes/${id}`);
            } else {
                // 离线模式，标记为删除
                await offlineManager.addToSyncQueue('delete', id);
            }
        } catch (error) {
            throw error;
        }
    },

    async syncNote(id: number): Promise<Note> {
        try {
            const response = await api.put(`/notes/${id}/sync`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    async searchNotes(query: string): Promise<Note[]> {
        try {
            const response = await api.get(`/notes/search?q=${encodeURIComponent(query)}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },



    async getTags(): Promise<any[]> {
        try {
            const response = await apiUtils.cachedRequest(
                'tags',
                () => api.get('/tags'),
                5 * 60 * 1000 // 5分钟缓存
            );
            return response.data;
        } catch (error) {
            return [];
        }
    },

    async getStats(): Promise<NoteStat> {
        try {
            const response = await api.get('/notes/stats');
            return response.data;
        } catch (error) {
            return {
                totalCount: 0,
                categoryStats: [],
                monthlyStats: [],
                tagStats: []
            };
        }
    },

    // 网页版特有功能

    // 批量操作
    async batchUpdateNotes(noteIds: number[], updates: Partial<Note>): Promise<void> {
        const requests = noteIds.map(id => () => this.updateNote(id, updates as any));
        await apiUtils.batchRequest(requests, 3);
    },

    // 导出笔记
    async exportNotes(format: 'json' | 'markdown' = 'json'): Promise<Blob> {
        const notes = await this.getAllNotes();

        if (format === 'json') {
            const jsonData = JSON.stringify(notes, null, 2);
            return new Blob([jsonData], { type: 'application/json' });
        } else {
            const markdownData = notes.map(note =>
                `# ${note.title}\n\n${note.content}\n\n---\n\n`
            ).join('');
            return new Blob([markdownData], { type: 'text/markdown' });
        }
    },

    // 导入笔记
    async importNotes(file: File): Promise<number> {
        const text = await file.text();

        try {
            const notes = JSON.parse(text);
            let importedCount = 0;

            for (const note of notes) {
                try {
                    await this.createNote({
                        title: note.title || '未命名',
                        content: note.content || '',
                        category: note.category || '默认',
                        tags: note.tags || [],
                        tagId: note.tagId
                    });
                    importedCount++;
                } catch (error) {
                    // 静默处理单个笔记导入失败
                }
            }

            return importedCount;
        } catch (error) {
            throw new Error('文件格式不正确');
        }
    }
};

export default noteService;
